import React, { useMemo } from "react";

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const HOUR_WIDTH = 32;
const STATION_COLUMN_WIDTH = 200;
const ROW_HEIGHT = 56;
const WEEKDAYS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const GROUP_ORDER = ["Касса", "Кухня", "Панировка", "Зал", "Ночь"];

function formatShortDate(dateString) {
  if (!dateString) return "";
  const iso = String(dateString).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function weekdayShort(dateString) {
  const iso = String(dateString).slice(0, 10);
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return "";
  return WEEKDAYS[new Date(year, month - 1, day).getDay()];
}

function listDatesInclusive(startDate, endDate) {
  if (!startDate || !endDate || startDate > endDate) return [];
  const dates = [];
  const [ys, ms, ds] = startDate.split("-").map(Number);
  const [ye, me, de] = endDate.split("-").map(Number);
  const current = new Date(ys, ms - 1, ds);
  const last = new Date(ye, me - 1, de);
  while (current <= last) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    const day = String(current.getDate()).padStart(2, "0");
    dates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function shiftTone(ts) {
  if (ts?.manual || ts?.group_station_name === "Ночь") return "night";
  switch (ts?.group_station_name) {
    case "Касса":
      return "kassa";
    case "Кухня":
      return "kitchen";
    case "Панировка":
      return "bread";
    case "Зал":
      return "hall";
    default:
      return "shift";
  }
}

function receiptsAt(receipts, dateStr, hour) {
  const dt = new Date(dateStr);
  dt.setHours(hour);
  return Number(receipts[dt.getTime()]?.receipts) || 0;
}

function heatLevel(count, max) {
  if (!max || !count) return 0;
  const t = count / max;
  if (t <= 0.2) return 1;
  if (t <= 0.4) return 2;
  if (t <= 0.6) return 3;
  if (t <= 0.8) return 4;
  return 5;
}

function isSameShift(a, b) {
  if (!a?.id || !b?.id) return false;
  return (
    a.id === b.id &&
    a.date === b.date &&
    a.station_name === b.station_name &&
    a.start === b.start
  );
}

function stationKey(shift) {
  return `${shift.group_station_name || "Прочее"}|${shift.station_name || "Станция"}`;
}

function getDisplayRange(shift, weekDates) {
  const dayIndex = weekDates.indexOf(shift.date);
  const start = Number(shift.start);
  const end = Number(shift.end);
  if (dayIndex < 0 || Number.isNaN(start) || Number.isNaN(end)) return null;

  const plusBefore = Number(shift.plus_before) || 0;
  const plusAfter = Number(shift.plus_after) || 0;
  let baseDuration = Number(shift.hours);
  if (!baseDuration) {
    if (end === 0) baseDuration = 24 - start;
    else if (end > start) baseDuration = end - start;
    else baseDuration = 24 - start + end;
  }

  const totalHours = weekDates.length * 24;
  const rawStart = dayIndex * 24 + start - plusBefore;
  const clampedStart = Math.max(0, rawStart);
  const hiddenBefore = clampedStart - rawStart;
  const visiblePlusBefore = Math.max(0, plusBefore - hiddenBefore);
  const rawEnd = clampedStart + visiblePlusBefore + baseDuration + plusAfter;
  const clampedEnd = Math.min(rawEnd, totalHours);
  if (clampedEnd <= clampedStart) return null;

  const mainStart = clampedStart + visiblePlusBefore;
  const mainDuration = Math.max(0, Math.min(baseDuration, clampedEnd - mainStart));
  const plusAfterStart = mainStart + mainDuration;
  const visiblePlusAfter = Math.max(0, clampedEnd - plusAfterStart);

  return {
    start: clampedStart,
    duration: clampedEnd - clampedStart,
    plusBefore: visiblePlusBefore,
    main: mainDuration,
    plusAfter: visiblePlusAfter,
  };
}

function slotsLabel(count) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} слот`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `${count} слота`;
  }
  return `${count} слотов`;
}

function formatHourRange(shift) {
  const start = Number(shift.start) - (Number(shift.plus_before) || 0);
  const endRaw = Number(shift.end) + (Number(shift.plus_after) || 0);
  const end = endRaw < 24 ? endRaw : endRaw - 24;
  return `${start}–${end}`;
}

const AutoDepWeek = ({
  shifts = [],
  startDate,
  endDate,
  receipts = {},
  selectedShift = null,
  onSelectShift,
  onDragStart,
  onAddNight,
}) => {
  const weekDates = useMemo(
    () => listDatesInclusive(startDate, endDate),
    [startDate, endDate],
  );

  const stations = useMemo(() => {
    const map = new Map();
    shifts.forEach((shift) => {
      const key = stationKey(shift);
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: shift.station_name || "Станция",
          group: shift.group_station_name || "Прочее",
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const ga = GROUP_ORDER.indexOf(a.group);
      const gb = GROUP_ORDER.indexOf(b.group);
      const groupCmp = (ga === -1 ? 99 : ga) - (gb === -1 ? 99 : gb);
      if (groupCmp) return groupCmp;
      return a.name.localeCompare(b.name, "ru");
    });
  }, [shifts]);

  const shiftsByStation = useMemo(() => {
    const map = {};
    stations.forEach((station) => {
      map[station.key] = [];
    });
    shifts.forEach((shift) => {
      const range = getDisplayRange(shift, weekDates);
      if (!range) return;
      const key = stationKey(shift);
      if (!map[key]) map[key] = [];
      map[key].push({ shift, ...range });
    });
    Object.values(map).forEach((list) => list.sort((a, b) => a.start - b.start));
    return map;
  }, [stations, shifts, weekDates]);

  const groupedStations = useMemo(() => {
    const groups = [];
    stations.forEach((station) => {
      const last = groups[groups.length - 1];
      if (!last || last.name !== station.group) {
        groups.push({ name: station.group, stations: [station] });
      } else {
        last.stations.push(station);
      }
    });
    return groups;
  }, [stations]);

  const hourHeat = useMemo(() => {
    const map = {};
    weekDates.forEach((date) => {
      const counts = HOURS.map((hour) => receiptsAt(receipts, date, hour));
      const max = Math.max(0, ...counts);
      map[date] = counts.map((count) => heatLevel(count, max));
    });
    return map;
  }, [weekDates, receipts]);

  if (!weekDates.length) {
    return <div className="schedule-chk-empty">Выберите период</div>;
  }

  if (!stations.length) {
    return (
      <div className="schedule-chk-empty">
        Нет слотов за выбранную неделю
      </div>
    );
  }

  const totalHours = weekDates.length * 24;
  const timelineWidth = totalHours * HOUR_WIDTH;
  const dayWidth = 24 * HOUR_WIDTH;

  return (
    <div className="chk-week autodep-week">
      <p className="chk-week-hint">
        Строка — станция, полоска — слот за неделю. Ночь через полночь не
        режется. Клик открывает правку, край можно потянуть.
      </p>
      <div className="chk-week-scroll">
        <div
          className="chk-week-grid"
          style={{ width: STATION_COLUMN_WIDTH + timelineWidth }}
        >
          <div
            className="chk-week-corner"
            style={{ width: STATION_COLUMN_WIDTH }}
          >
            Станция
          </div>
          <div
            className="chk-week-header autodep-week-header"
            style={{
              marginLeft: STATION_COLUMN_WIDTH,
              width: timelineWidth,
            }}
          >
            <div className="chk-week-day-header">
              {weekDates.map((date) => (
                <div
                  key={date}
                  className="chk-week-day autodep-week-day"
                  style={{ width: dayWidth }}
                >
                  <strong>
                    {weekdayShort(date)} {formatShortDate(date)}
                  </strong>
                  {onAddNight ? (
                    <button
                      type="button"
                      className="autodep-week-add-night"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModal"
                      onClick={() => onAddNight(date)}
                      title="Добавить ночника"
                    >
                      +
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="chk-week-hour-header">
              {weekDates.flatMap((date) =>
                HOURS.map((hour) => (
                  <div
                    key={`${date}-${hour}`}
                    className={`chk-week-hour autodep-week-hour is-heat-${hourHeat[date]?.[hour] ?? 0}`}
                    style={{ width: HOUR_WIDTH }}
                    title={`${formatShortDate(date)} ${hour}:00 · ${receiptsAt(receipts, date, hour)} чеков`}
                  >
                    {hour}
                  </div>
                )),
              )}
            </div>
          </div>

          <div className="chk-week-body">
            {groupedStations.map((group) => (
              <React.Fragment key={group.name}>
                <div
                  className="autodep-week-group"
                  style={{ height: 32, width: STATION_COLUMN_WIDTH + timelineWidth }}
                >
                  <div
                    className="autodep-week-group-label"
                    style={{ width: STATION_COLUMN_WIDTH }}
                  >
                    {group.name}
                  </div>
                </div>
                {group.stations.map((station) => {
                  const stationShifts = shiftsByStation[station.key] || [];
                  return (
                    <div
                      key={station.key}
                      className="chk-week-row"
                      style={{ height: ROW_HEIGHT }}
                    >
                      <div
                        className="chk-week-employee"
                        style={{ width: STATION_COLUMN_WIDTH }}
                      >
                        <span className="chk-week-employee-name">
                          {station.name}
                        </span>
                        <span className="chk-week-employee-post">
                          {slotsLabel(stationShifts.length)}
                        </span>
                      </div>
                      <div
                        className="chk-week-track"
                        style={{
                          marginLeft: STATION_COLUMN_WIDTH,
                          width: timelineWidth,
                          height: ROW_HEIGHT,
                          "--hour-width": `${HOUR_WIDTH}px`,
                          "--day-width": `${dayWidth}px`,
                        }}
                      >
                        {stationShifts.map((item) => {
                          const tone = shiftTone(item.shift);
                          const selected = isSameShift(selectedShift, item.shift);
                          const time = formatHourRange(item.shift);
                          return (
                            <div
                              key={`${item.shift.id}-${item.shift.date}-${item.shift.start}`}
                              className={`autodep-week-shift autodep-shift--${tone}${selected ? " is-selected" : ""}`}
                              style={{
                                left: item.start * HOUR_WIDTH + 1,
                                width: Math.max(item.duration * HOUR_WIDTH - 2, 20),
                              }}
                              title={`${station.name}\n${item.shift.date} · ${time}`}
                              onClick={() => onSelectShift?.(item.shift)}
                            >
                              {item.plusBefore > 0 ? (
                                <span
                                  className="autodep-week-plus"
                                  style={{ flex: item.plusBefore }}
                                />
                              ) : null}
                              <span
                                className="autodep-week-main"
                                style={{ flex: Math.max(item.main, 0.2) }}
                              >
                                <span className="autodep-week-shift-time">{time}</span>
                              </span>
                              {item.plusAfter > 0 ? (
                                <span
                                  className="autodep-week-plus"
                                  style={{ flex: item.plusAfter }}
                                />
                              ) : null}
                              <span
                                className="autodep-handle autodep-handle-start"
                                onMouseDown={(event) =>
                                  onDragStart?.(event, item.shift, "start")
                                }
                                onClick={(event) => event.stopPropagation()}
                                title="Потяните, чтобы изменить начало"
                              />
                              <span
                                className="autodep-handle autodep-handle-end"
                                onMouseDown={(event) =>
                                  onDragStart?.(event, item.shift, "end")
                                }
                                onClick={(event) => event.stopPropagation()}
                                title="Потяните, чтобы изменить конец"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoDepWeek;
